import {
  getChargeCostTypeLabel,
  useChargeCostDefinitions,
  type AdditionalRateTypeValue,
  type CreateCustomerAdditionalChargeRequest,
} from '@my/tms-api';
import { cn, ErrorBanner, Loading } from '@my/ui';
import { Check, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { AdditionalRow } from './ratesChargesMock';
import { Toggle } from './Toggle';

export interface AdditionalsFormProps {
  organisationId: string;
  customerPartyId: string;
  /** Currency — symbol for the inputs, ISO code for the request. */
  currencySymbol: string;
  currencyIsoCode: string;
  /** Charges already on this customer — can't be added a second time. */
  existing: AdditionalRow[];
  onCancel: () => void;
  onSave: (requests: CreateCustomerAdditionalChargeRequest[]) => void;
}

/** How an additional charge is priced (display label). */
type RateType = 'Flat' | 'Per hour' | '% of base';
const RATE_TYPES: readonly RateType[] = ['Flat', 'Per hour', '% of base'];
const RATE_TYPE_WIRE: Record<RateType, AdditionalRateTypeValue> = {
  Flat: 'Flat',
  'Per hour': 'PerHour',
  '% of base': 'PercentOfBase',
};

interface AdditionalDraft {
  included: boolean;
  rateType: RateType;
  rate: string;
  fsApplicable: boolean;
}

const EMPTY_DRAFT: AdditionalDraft = {
  included: false,
  rateType: 'Flat',
  rate: '',
  fsApplicable: false,
};

/**
 * "Add additional charges" — presentation prototype.
 *
 * Sources the org's charge/cost catalogue (Accounting → Charges & Costs)
 * filtered to those that apply to Contacts and aren't Transport/Fuel. Each is
 * **optional per customer**: the operator ticks the ones this customer gets
 * and sets the rate type / rate / FS-applicable. Nothing is persisted.
 */
export function AdditionalsForm({
  organisationId,
  customerPartyId,
  currencySymbol,
  currencyIsoCode,
  existing,
  onCancel,
  onSave,
}: AdditionalsFormProps) {
  const defsQuery = useChargeCostDefinitions(organisationId);

  // A charge already on this customer can't be added again. Match on the
  // source definition id, falling back to display name for legacy/seed rows.
  const alreadyAdded = (defId: string, displayName: string) =>
    existing.some(
      (r) =>
        (r.chargeDefId && r.chargeDefId === defId) ||
        r.charge.trim().toLowerCase() === displayName.trim().toLowerCase(),
    );

  // Eligible = applies to Contacts, active, and NOT Transport/Fuel (those have
  // their own dedicated grids).
  const eligible = useMemo(
    () =>
      (defsQuery.data ?? []).filter(
        (d) =>
          d.status === 'Active' &&
          d.appliesTo === 'Contacts' &&
          d.type !== 'TransportCharge' &&
          d.type !== 'FuelSurcharge',
      ),
    [defsQuery.data],
  );

  const [drafts, setDrafts] = useState<Record<string, AdditionalDraft>>({});
  const draftFor = (id: string): AdditionalDraft => drafts[id] ?? EMPTY_DRAFT;
  const patch = (id: string, p: Partial<AdditionalDraft>) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] ?? EMPTY_DRAFT), ...p } }));

  const canSave = eligible.some((d) => {
    const dr = draftFor(d.id);
    return dr.included && dr.rate.trim() !== '' && !alreadyAdded(d.id, d.displayName);
  });

  const handleSave = () => {
    if (!canSave) return;
    const requests: CreateCustomerAdditionalChargeRequest[] = eligible
      .map((d) => ({ d, dr: draftFor(d.id) }))
      .filter(({ d, dr }) => dr.included && dr.rate.trim() !== '' && !alreadyAdded(d.id, d.displayName))
      .map(({ d, dr }) => ({
        organisationId,
        customerPartyId,
        chargeCostDefinitionId: d.id,
        chargeDisplayName: d.displayName,
        rateType: RATE_TYPE_WIRE[dr.rateType],
        rate: Number(dr.rate) || 0,
        currencyIsoCode,
        fsApplicable: dr.fsApplicable,
      }));
    onSave(requests);
  };

  return (
    <div className="rounded-md border border-blue-200 bg-white">
      <div className="border-b border-blue-100 bg-blue-50/70 px-3 py-2 text-sm font-semibold text-blue-700">
        Add additional charges
      </div>

      <div className="space-y-3 p-3">
        <p className="text-[11px] text-slate-500">
          From Accounting → Charges &amp; Costs (applies to Contacts). Tick the charges this customer
          gets and set each one.
        </p>

        {defsQuery.isPending && <Loading label="Loading charges" />}
        {defsQuery.isError && (
          <ErrorBanner title="Failed to load charges" message={defsQuery.error.message} />
        )}

        {defsQuery.isSuccess &&
          (eligible.length === 0 ? (
            <p className="rounded-md border border-slate-200 px-3 py-3 text-xs italic text-slate-400">
              No additional charges configured for Contacts in Accounting → Charges &amp; Costs.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full table-auto text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="w-8 px-2 py-1.5" />
                    <th className="px-3 py-1.5 text-left font-medium">Charge</th>
                    <th className="px-2 py-1.5 text-left font-medium">Rate type</th>
                    <th className="px-2 py-1.5 text-left font-medium">Rate</th>
                    <th className="px-2 py-1.5 text-left font-medium">FS applicable</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {eligible.map((d) => {
                    const dr = draftFor(d.id);
                    const added = alreadyAdded(d.id, d.displayName);
                    const off = added || !dr.included;
                    return (
                      <tr key={d.id} className={cn(off && 'opacity-60')}>
                        <td className="px-2 py-1.5 align-middle">
                          <input
                            type="checkbox"
                            checked={!added && dr.included}
                            disabled={added}
                            onChange={(e) => patch(d.id, { included: e.target.checked })}
                            className="h-3.5 w-3.5 rounded border-slate-300 disabled:opacity-50"
                          />
                        </td>
                        <td className="px-3 py-1.5 align-middle">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-slate-900">{d.displayName}</span>
                            {added && (
                              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-500">
                                Already added
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-400">
                            {getChargeCostTypeLabel(d.type)}
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <select
                            value={dr.rateType}
                            disabled={off}
                            onChange={(e) => patch(d.id, { rateType: e.target.value as RateType })}
                            className="w-full rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-50"
                          >
                            {RATE_TYPES.map((rt) => (
                              <option key={rt} value={rt}>
                                {rt}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="inline-flex items-center gap-1">
                            {dr.rateType !== '% of base' && <span className="text-slate-400">{currencySymbol}</span>}
                            <input
                              value={dr.rate}
                              disabled={off}
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder={dr.rateType === '% of base' ? '0' : '0.00'}
                              onChange={(e) => patch(d.id, { rate: e.target.value })}
                              className="w-20 rounded-sm border border-slate-300 bg-white px-2 py-1 text-right text-xs tabular-nums focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-50"
                            />
                            {dr.rateType === '% of base' && <span className="text-slate-400">%</span>}
                            {dr.rateType === 'Per hour' && <span className="text-slate-400">/ hr</span>}
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <Toggle
                            checked={dr.fsApplicable}
                            onChange={(v) => patch(d.id, { fsApplicable: v })}
                            label={dr.fsApplicable ? 'Yes' : 'No'}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}

        {/* Actions */}
        <div className="flex items-center justify-end gap-1 border-t border-slate-200 pt-3">
          <button
            type="button"
            aria-label="Cancel"
            onClick={onCancel}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="Save"
            onClick={handleSave}
            disabled={!canSave}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
